import { FlexBox, SelectionList } from '@dixa/lib-shared-habla';
import Fuse from 'fuse.js';
import { ConversationLanguageCode } from 'lib-shared-languages';
import { ReactNode, useMemo } from 'react';

import {
  languageCodesToSelectionListValue,
  LanguageSelectionListValue,
} from 'utils/useLanguageSelectionList';

const minMatchCharLength = 2;

type UseLanguageSelectionListItemsProps = {
  /** The revert item label. */
  readonly revertLabel?: ReactNode;
  /** The function for sorting the languages. */
  readonly sortFn?: (
    a: LanguageSelectionListValue,
    b: LanguageSelectionListValue,
  ) => number;
  /** The search term for filtering languages. */
  readonly searchTerm: string;
  /** An array of recently used language codes. */
  readonly recentlyUsedLanguagesCodes?: readonly ConversationLanguageCode[];
  /** Determines whether the revert item should be shown. */
  readonly showRevert?: boolean;
  /** An array of available languages for selection. */
  readonly languages: readonly LanguageSelectionListValue[];
  /** The function for rendering the content of the list item. */
  readonly itemContentFn?: (language: LanguageSelectionListValue) => ReactNode;
};

/**
 * Creates list of SelectionList Items and Dividers based on the language
 * preferences. This helps us share SelectionList items across different types
 * of selection lists (e.g. SelectionList and InputSelectionList).
 */
export function useLanguageSelectionListItems(
  props: UseLanguageSelectionListItemsProps,
): JSX.Element {
  const {
    revertLabel = 'Show original message',
    searchTerm,
    recentlyUsedLanguagesCodes,
    itemContentFn = (item) => (
      <LanguageSelectionListItemContent language={item} />
    ),
    languages,
    showRevert,
    sortFn = (a, b) => a.name.localeCompare(b.name),
  } = props;

  const recentlyUsedLanguages = useMemo(
    () => languageCodesToSelectionListValue(recentlyUsedLanguagesCodes ?? []),
    [recentlyUsedLanguagesCodes],
  );

  const recentlyUsedItems = useMemo(
    () =>
      searchTerm || !recentlyUsedLanguages.length ? undefined : (
        <>
          <SelectionList.Divider label="Recently used languages" />

          {recentlyUsedLanguages.map((recentlyUsedLanguage) => (
            <SelectionList.Item
              key={`recentlyUsed-${recentlyUsedLanguage.code}`}
              value={recentlyUsedLanguage}
            >
              {itemContentFn(recentlyUsedLanguage)}
            </SelectionList.Item>
          ))}

          <SelectionList.Divider label="All languages" />
        </>
      ),
    [itemContentFn, recentlyUsedLanguages, searchTerm],
  );

  const sortedLanguages = useMemo(
    () => [...languages].sort(sortFn),
    [languages, sortFn],
  );

  const filteredLanguages = useMemo(() => {
    const fuse = new Fuse(sortedLanguages, {
      keys: ['name'],
      threshold: 0.2,
      minMatchCharLength,
      distance: 200,
    });

    if (searchTerm.length >= minMatchCharLength) {
      const fuseResults = fuse.search(searchTerm).map(({ item }) => item);
      return fuseResults.length > 0 ? fuseResults : sortedLanguages;
    }

    return sortedLanguages;
  }, [searchTerm, sortedLanguages]);

  const allItems = useMemo(
    () =>
      filteredLanguages.map((language) => (
        <SelectionList.Item key={`all-${language.code}`} value={language}>
          {itemContentFn(language)}
        </SelectionList.Item>
      )),
    [filteredLanguages, itemContentFn],
  );

  return (
    <>
      {showRevert && (
        <SelectionList.Item value="revert">{revertLabel}</SelectionList.Item>
      )}

      {recentlyUsedItems}

      {allItems}
    </>
  );
}

type LanguageSelectionListItemContentProps = {
  /** The selection list value. */
  language: LanguageSelectionListValue;
  /** The content to insert at the end of the content. */
  endSlot?: ReactNode;
};

export const LanguageSelectionListItemContent = ({
  language,
  endSlot,
}: LanguageSelectionListItemContentProps): JSX.Element => {
  return (
    <FlexBox alignItems="center" justifyContent="space-between">
      {language.name}
      {endSlot}
    </FlexBox>
  );
};
